import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import useBodyScrollLock from "../../../hooks/useBodyScrollLock";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmt = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return `${String(dt.getDate()).padStart(2, "0")} ${MONTHS[dt.getMonth()]}`;
};

const STEP_LABELS = ["Searching airlines & routes", "Comparing fares", "Applying best deals"];

export const FlightSearchLoader = ({
  fromCode = "",
  fromCity = "",
  toCode = "",
  toCity = "",
  departDate = "",
  returnDate = "",
  adult = 0,
  child = 0,
  infant = 0,
  travelClass = "",
  isMultiCity = false,
  multiCitySegments = [],
}) => {
  const [pct, setPct] = useState(0);

  useBodyScrollLock();

  useEffect(() => {
    const t = setInterval(() => {
      setPct((p) => {
        if (p >= 90) return 90;
        const np = p + 0.8 * (p > 80 ? 0.3 : 1);
        return np >= 90 ? 90 : np;
      });
    }, 60);
    return () => clearInterval(t);
  }, []);

  const p = Math.round(pct);
  const stage = pct < 35 ? 0 : pct < 75 ? 1 : 2;

  const plane = useMemo(() => {
    const t = pct / 100;
    const seg = Math.min(3, Math.floor(t * 4));
    const s = t * 4 - seg;
    const dir = seg % 2 === 0 ? -1 : 1;
    const x = t * 100;
    const yU = 24 + dir * 56 * s * (1 - s);
    const y = (yU / 48) * 100;
    const rot = (Math.atan2(dir * 56 * (1 - 2 * s), 50 * 1.3) * 180) / Math.PI;
    return { x: x.toFixed(2), y: y.toFixed(2), rot: rot.toFixed(1) };
  }, [pct]);

  const stations = useMemo(() => {
    if (!isMultiCity || !multiCitySegments || multiCitySegments.length === 0) return [];
    const list = [];
    multiCitySegments.forEach((seg, idx) => {
      if (idx === 0) {
        if (seg?.fromCode) list.push({ code: seg.fromCode, city: seg.fromCity });
        if (seg?.toCode) list.push({ code: seg.toCode, city: seg.toCity });
      } else {
        const prev = list[list.length - 1];
        if (prev?.code !== seg?.fromCode && seg?.fromCode) {
          list.push({ code: seg.fromCode, city: seg.fromCity });
        }
        if (seg?.toCode) {
          list.push({ code: seg.toCode, city: seg.toCity });
        }
      }
    });
    return list;
  }, [isMultiCity, multiCitySegments]);

  const dateLabel = useMemo(() => {
    if (isMultiCity && multiCitySegments.length > 0) {
      const firstDate = multiCitySegments[0]?.departDate;
      const lastDate = multiCitySegments[multiCitySegments.length - 1]?.departDate;
      const a = fmt(firstDate);
      const b = fmt(lastDate);
      if (a && b && a !== b) return `${a} – ${b}`;
      if (a) return a;
    }
    const a = fmt(departDate);
    const b = fmt(returnDate);
    return b ? `${a} – ${b}` : a;
  }, [isMultiCity, multiCitySegments, departDate, returnDate]);

  const paxLabel = useMemo(() => {
    const parts = [];
    if (adult) parts.push(`${adult} ${adult > 1 ? "Adults" : "Adult"}`);
    if (child) parts.push(`${child} ${child > 1 ? "Children" : "Child"}`);
    if (infant) parts.push(`${infant} ${infant > 1 ? "Infants" : "Infant"}`);
    return parts.join(", ");
  }, [adult, child, infant]);

  return createPortal(
    <div className="fsl-overlay">
      <style>{`
        .fsl-overlay{position:fixed;inset:0;background:rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center;padding:24px;z-index:99999;overflow:auto}
        .fsl-modal{width:100%;max-width:500px;background:#fff;border-radius:16px;overflow:hidden;box-shadow:0 24px 60px rgba(15,25,55,.24);animation:fsl-pop .4s cubic-bezier(.2,.8,.2,1) both}
        .fsl-head{background:#F3F7FF;border-bottom:1px solid #E3EAFB;padding:28px 30px 24px;display:flex;flex-direction:column;gap:20px}
        .fsl-route{display:grid;grid-template-columns:auto minmax(0,1fr) auto;align-items:center;gap:20px}
        .fsl-code{font-size:30px;font-weight:800;line-height:1;letter-spacing:-.01em;color:#1B1F2B}
        .fsl-city{font-size:13px;color:#5B6070;margin-top:4px}
        .fsl-end.right{text-align:right}
        .fsl-path{position:relative;height:48px}
        .fsl-plane{position:absolute;width:28px;height:28px;border-radius:50%;background:#fff;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(47,107,245,.25)}
        .fsl-chips{display:flex;flex-wrap:wrap;gap:8px;font-size:13px;font-weight:600;color:#1B1F2B}
        .fsl-chip{background:#fff;border:1px solid #E3EAFB;padding:6px 12px;border-radius:999px}
        .fsl-chip--badge{background:#2F6BF5!important;color:#fff!important;border-color:#2F6BF5!important;font-weight:700!important}
        .fsl-route-mc{width:100%;position:relative;padding:6px 0 2px}
        .fsl-mc-steps{display:flex;width:100%;align-items:flex-start;position:relative}
        .fsl-mc-step{flex:1;display:flex;flex-direction:column;align-items:center;position:relative;min-width:0}
        .fsl-mc-top-line{display:flex;align-items:center;width:100%;height:16px;position:relative}
        .fsl-mc-segment-line{flex:1;border-top:2px dashed #B9CCF9;height:0}
        .fsl-mc-segment-line.is-hidden{border-top-color:transparent}
        .fsl-mc-dot{width:14px;height:14px;border-radius:50%;background:#2F6BF5;border:3px solid #fff;box-shadow:0 0 0 1.5px #B9CCF9;flex-shrink:0;box-sizing:border-box;z-index:1}
        .fsl-mc-plane-float{position:absolute;width:24px;height:24px;border-radius:50%;background:#fff;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(47,107,245,.25);z-index:3;top:8px;transform:translate(-50%,-50%)}
        .fsl-mc-label-box{display:flex;flex-direction:column;align-items:center;text-align:center;margin-top:8px;width:100%;padding:0 2px}
        .fsl-mc-code{font-size:22px;font-weight:800;color:#1B1F2B;line-height:1;letter-spacing:-.01em;text-align:center}
        .fsl-mc-code.compact{font-size:18px}
        .fsl-mc-city{font-size:12px;font-weight:500;color:#5B6070;margin-top:4px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100%;text-align:center}
        .fsl-body{padding:24px 30px 28px;display:flex;flex-direction:column;gap:20px}
        .fsl-title{font-size:18px;font-weight:700;color:#1B1F2B}
        .fsl-steps{display:flex;flex-direction:column;gap:12px}
        .fsl-step{display:flex;align-items:center;gap:12px;font-size:14px;font-weight:600}
        .fsl-ic{width:20px;height:20px;border-radius:50%;box-sizing:border-box;flex:none;display:flex;align-items:center;justify-content:center}
        .fsl-ic.done{background:#2F6BF5}
        .fsl-ic.spin{border:2px solid #D5E0FB;border-top-color:#2F6BF5;animation:fsl-spin .8s linear infinite}
        .fsl-ic.pend{border:2px solid #E4E6EB}
        .fsl-track{height:6px;border-radius:999px;background:#EEF1F6;overflow:hidden}
        .fsl-fill{height:100%;background:#2F6BF5;border-radius:999px;transition:width .2s linear}
        .fsl-progrow{display:flex;justify-content:space-between;font-size:12px;font-weight:600;color:#5B6070;margin-top:8px}
        @keyframes fsl-pop{from{opacity:0;transform:translateY(16px) scale(.97)}to{opacity:1;transform:none}}
        @keyframes fsl-spin{to{transform:rotate(360deg)}}
      `}</style>

      <div className="fsl-modal">
        <div className="fsl-head">
          {isMultiCity && stations.length > 1 ? (
            <div className="fsl-route-mc">
              <div className="fsl-mc-steps">
                {/* anchored inside .fsl-mc-steps so its `top` is measured from the
                    same box that holds the dashed line, not from .fsl-route-mc
                    (whose padding would push the plane off the line) */}
                {stations.length > 1 && (
                  <div
                    className="fsl-mc-plane-float"
                    style={{
                      left: `${((0.5 + (pct / 100) * (stations.length - 1)) / stations.length) * 100}%`,
                    }}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="#2F6BF5">
                      <path d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5z" transform="rotate(90 12 12)" />
                    </svg>
                  </div>
                )}

                {stations.map((stn, idx) => {
                  const isFirst = idx === 0;
                  const isLast = idx === stations.length - 1;
                  return (
                    <div key={`mc-stn-${idx}`} className="fsl-mc-step">
                      <div className="fsl-mc-top-line">
                        <div className={`fsl-mc-segment-line ${isFirst ? 'is-hidden' : ''}`} />
                        <div className="fsl-mc-dot" />
                        <div className={`fsl-mc-segment-line ${isLast ? 'is-hidden' : ''}`} />
                      </div>
                      <div className="fsl-mc-label-box">
                        <div className={`fsl-mc-code ${stations.length > 3 ? 'compact' : ''}`}>{stn.code || "—"}</div>
                        {stn.city ? <div className="fsl-mc-city" title={stn.city}>{stn.city}</div> : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="fsl-route">
              <div className="fsl-end">
                <div className="fsl-code">{fromCode || "—"}</div>
                {fromCity ? <div className="fsl-city">{fromCity}</div> : null}
              </div>
              <div className="fsl-path">
                <svg viewBox="0 0 200 48" preserveAspectRatio="none" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", overflow: "visible" }}>
                  <path d="M0 24 Q25 -4 50 24 T100 24 T150 24 T200 24" fill="none" stroke="#B9CCF9" strokeWidth="1.5" strokeDasharray="3 5" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
                </svg>
                <div className="fsl-plane" style={{ left: `${plane.x}%`, top: `${plane.y}%`, transform: `translate(-50%,-50%) rotate(${plane.rot}deg)` }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="#2F6BF5"><path d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5z" transform="rotate(90 12 12)" /></svg>
                </div>
              </div>
              <div className="fsl-end right">
                <div className="fsl-code">{toCode || "—"}</div>
                {toCity ? <div className="fsl-city">{toCity}</div> : null}
              </div>
            </div>
          )}
          <div className="fsl-chips">
            {isMultiCity ? <span className="fsl-chip fsl-chip--badge">Multi-City</span> : null}
            {dateLabel ? <span className="fsl-chip">{dateLabel}</span> : null}
            {paxLabel ? <span className="fsl-chip">{paxLabel}</span> : null}
            {travelClass ? <span className="fsl-chip">{travelClass}</span> : null}
          </div>
        </div>

        <div className="fsl-body">
          <div className="fsl-title">Hold on, we're fetching flights for you</div>

          <div className="fsl-steps">
            {STEP_LABELS.map((label, i) => (
              <div className="fsl-step" key={i}>
                {i < stage ? (
                  <span className="fsl-ic done"><svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="2"><path d="M2 6.5 5 9l5-6" /></svg></span>
                ) : i === stage ? (
                  <span className="fsl-ic spin" />
                ) : (
                  <span className="fsl-ic pend" />
                )}
                <span style={{ color: i > stage ? "#9AA0AD" : "#1B1F2B" }}>{label}</span>
              </div>
            ))}
          </div>

          <div>
            <div className="fsl-track"><div className="fsl-fill" style={{ width: p + "%" }} /></div>
            <div className="fsl-progrow"><span>Comparing 480+ airlines</span><span>{p}%</span></div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default FlightSearchLoader;
